import { Permission } from 'interfaces/permissionOption';
import { InternalUser, UserCredentials, UserCredentialsPaginatedResponse } from 'interfaces/userCredentials';
import { stringify } from 'qs';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';

export interface UserManagementOverviewSummary {
  scopeLabel: string;
  totalUsers: number;
  totalExternalUsers: number;
  totalInternalUsers: number;
  totalLabs: number;
  totalStudies: number;
  totalCases: number;
  totalSlides: number;
  activeUsers30d: number;
  activeLabs30d: number;
  newStudies30d: number;
}

export interface UserManagementActivityWindow {
  key: string;
  label: string;
  days: number;
  activeUsers: number;
  activeExternalUsers: number;
  activeInternalUsers: number;
  activeLabs: number;
  newStudies: number;
}

export interface UserManagementActivityPoint {
  date: string;
  activeUsers: number;
  activeExternalUsers: number;
  activeInternalUsers: number;
  activeLabs: number;
  newStudies: number;
}

export interface UserManagementActivityTimeline {
  key: string;
  label: string;
  days: number;
  points: UserManagementActivityPoint[];
}

export interface UserManagementRankedUser {
  userId: string;
  userName: string | null;
  userEmail: string;
  isInternal: boolean;
  lastLogin: string | null;
  activeDays: number;
  loginEvents: number;
  labId: string | null;
  lastLoginCountry: string | null;
  lastLoginIp: string | null;
  lastLoginLocation: string | null;
}

export interface UserManagementUserSplitWindow {
  key: string;
  label: string;
  days: number;
  externalUsers: UserManagementRankedUser[];
  internalUsers: UserManagementRankedUser[];
}

export interface UserManagementStudySnapshot {
  studyId: string;
  studyName: string;
  labId: string | null;
  createdAt: string | null;
  casesCount: number;
  slidesCount: number;
}

export interface UserManagementStudiesWindow {
  key: string;
  label: string;
  days: number;
  studies: UserManagementStudySnapshot[];
}

export interface UserManagementOverviewResponse {
  canViewInternalMetrics: boolean;
  summary: UserManagementOverviewSummary;
  activityWindows: UserManagementActivityWindow[];
  activityTimelines: UserManagementActivityTimeline[];
  recentUsersByWindow: UserManagementUserSplitWindow[];
  mostUsedUsersByWindow: UserManagementUserSplitWindow[];
  activeUsersByWindow: UserManagementUserSplitWindow[];
  recentStudiesByWindow: UserManagementStudiesWindow[];
}

export const setUserCredentialsRolesAndPermissions = ({
  id,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  name,
  applyToAllLabs = false,
  ...userCredentialsParams
}: UserCredentials & { labId: string; applyToAllLabs?: boolean }) => {
  if (!id) {
    throw new Error('User ID is required');
  }
  return apiRequestHandlerPromise<string>({
    url: `users/credentials/${encodeURI(id)}`,
    method: 'POST',
    data: JSON.stringify({ ...userCredentialsParams, applyToAllLabs: Boolean(applyToAllLabs) }),
  });
};

export const getInternalUsers = () => {
  return apiRequestHandlerPromise<InternalUser[]>({
    url: `users/internal`,
    method: 'GET',
  });
};

export const getUserCredentialsResponse = (
  labId: string,
  {
    page = 0,
    perPage,
    ascending = true,
    sortField = 'email',
    listNucleaiUsers = false,
    searchQuery = '',
    studyId,
  }: {
    page?: number;
    perPage?: number;
    ascending?: boolean;
    sortField?: string;
    listNucleaiUsers?: boolean;
    searchQuery?: string;
    studyId?: string;
  } = {}
) => {
  return apiRequestHandlerPromise<UserCredentialsPaginatedResponse>({
    url: `users?${stringify({ labId, page, perPage, ascending, sortField, listNucleaiUsers, searchQuery, studyId })}`,
    method: 'GET',
  });
};

export const getUserLabPermissions = (labId: string, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<Permission[]>({
    url: `users/credentials?${stringify({ labId })}`,
    method: 'GET',
    signal,
  });
};

export interface CurrentUserResponse {
  userId: string;
  auth0UserId: string | null;
}

export const getCurrentUser = (signal?: AbortSignal) => {
  return apiRequestHandlerPromise<CurrentUserResponse>({
    url: 'users/me',
    method: 'GET',
    signal,
  });
};

export const getUserManagementOverview = (labId: string) => {
  return apiRequestHandlerPromise<UserManagementOverviewResponse>({
    url: `users/overview?${stringify({ labId })}`,
    method: 'GET',
  });
};
